import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { EmptyState } from '../../components/ui/PanelCard';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PanelCard from '../../components/ui/PanelCard';
import {
  ItemsBox, StepsList, KillTargets, itemShort, utcDate, SearchInput, TabHead, ITEM_KIND_LABEL, BOT_ASKS_BEFORE_FIRST_RUN,
  IconPlus, IconFlag, IconAlert, IconShield, IconBook, IconChevron, IconPackage,
} from './routeUi';

/**
 * ── A KÖZÖSSÉGI ROUTE-KÖNYVTÁR ("Route library" fül) ─────────────────────────
 *
 * Csak JÓVÁHAGYOTT route-ok (terv 8.). A "hozzáadás" független másolat a vevő
 * "Community" mappájába: a szerző későbbi módosítása nem írja át. Mivel egy
 * idegen route tárgyat használhat el, a hozzáadás előtt a felület kiírja, mit
 * (terv 4.10). A bot első futás előtti kérdése későbbi kör (Q5, 4.6 f) — addig
 * a felület nem ígéri (BOT_ASKS_BEFORE_FIRST_RUN).
 */

const REPORT_REASONS = [
  'It does not work',
  'It uses items it does not mention',
  'Misleading title or description',
  'Copied from someone else',
  'Spam',
];

export default function LibraryTab() {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [q, setQ] = useState('');
  const [map, setMap] = useState('');
  const [sort, setSort] = useState('added');
  const [noItems, setNoItems] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/library', {
        params: { q: q || undefined, map: map || undefined, sort, noItems: noItems ? 1 : undefined, page },
      });
      setData(res.data);
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not load the library.');
    } finally {
      setLoading(false);
    }
  }, [q, map, sort, noItems, page]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);   // a kereső gépelés közben ne kérdezzen minden betűre
    return () => clearTimeout(t);
  }, [load, q]);

  if (openId) {
    return <LibraryItem id={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  if (data && data.enabled === false) {
    return (
      <div className="card">
        <EmptyState
          icon={<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />}
          title="The route library is closed right now."
          hint="Your own routes are not affected."
        />
      </div>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  // A pálya angol neve (a backend szótárából), különben az azonosító.
  const mapLabel = (m) => data?.names?.maps?.[m]?.name || m;

  return (
    <div className="flex flex-col gap-6">
      <TabHead title="Route library">
        Routes other players shared, each checked by a tester or an admin. Adding one puts a copy into your
        Routes, and that copy never changes by itself afterwards.
      </TabHead>

      {/* A sablon `.libbar`-ja: kereső · pálya · rendezés, alatta a tárgy-szűrő. */}
      <div className="flex flex-col gap-3">
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_200px_180px]">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search title or author"
                       className="sm:col-span-2 lg:col-span-1" />
          <select className="select" aria-label="Map" value={map} onChange={e => { setMap(e.target.value); setPage(1); }}>
            <option value="">All maps</option>
            {[...(data?.maps || [])]
              .sort((a, b) => mapLabel(a).localeCompare(mapLabel(b)))
              .map(m => <option key={m} value={m}>{mapLabel(m)}</option>)}
          </select>
          <select className="select" aria-label="Sort" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
            <option value="added">Most added</option>
            <option value="new">Newest</option>
            <option value="updated">Recently updated</option>
          </select>
        </div>
        <label className="self-start flex items-center gap-2.5 text-[13px] text-gray-400 cursor-pointer">
          <input type="checkbox" className="checkbox" checked={noItems} onChange={e => { setNoItems(e.target.checked); setPage(1); }} />
          Only routes that use no items
        </label>
      </div>

      {loading && !data ? (
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]" aria-busy="true">
          {[0, 1, 2].map(i => <div key={i} className="skeleton h-[188px]" />)}
        </div>
      ) : data.items.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />}
            title={data.total === 0 && !q && !map && !noItems ? 'Nothing in the library yet.' : 'Nothing matches.'}
            hint="Share one of your own routes from the Routes page."
          />
        </div>
      ) : (
        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {data.items.map(it => (
            <article key={it.id} className="card px-5 py-[18px] flex flex-col gap-2.5 min-w-0">
              <div className="min-w-0">
                <h3>
                  <button type="button" onClick={() => setOpenId(it.id)}
                          className="text-left text-base font-semibold tracking-[-0.01em] text-gray-50 hover:text-brand-400 transition-colors break-words">
                    {it.title}
                  </button>
                </h3>
                <span className="text-[12.5px] text-gray-500">by {it.author}{it.mine ? ' (you)' : ''} · updated {utcDate(it.updatedAt)}</span>
              </div>

              {/* A pályák angol neve (a backend szótárából), különben az azonosító. */}
              <div className="flex flex-col gap-0.5 min-w-0">
                {it.maps.slice(0, 2).map(m => (
                  <span key={m} title={m}
                        className={data.names?.maps?.[m] ? 'text-[13.5px] text-gray-400 truncate' : 'font-mono text-[12px] text-gray-400 truncate'}>
                    {mapLabel(m)}
                  </span>
                ))}
                {it.maps.length > 2 && <span className="text-[12.5px] text-gray-600">+ {it.maps.length - 2} more</span>}
              </div>

              <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 font-mono text-xs text-gray-500">
                <span>{it.steps} {it.steps === 1 ? 'step' : 'steps'}</span>
                {it.kills > 0 && <span>{it.kills} kill target{it.kills > 1 ? 's' : ''}</span>}
                <span>{it.adds} {it.adds === 1 ? 'add' : 'adds'}</span>
              </div>

              <div className="mt-auto pt-2.5 border-t border-line-faint flex items-center justify-between gap-2.5">
                {it.items.length ? <ItemPill items={it.items} names={data.names} /> : <span className="badge-active">No items</span>}
                {it.inMine ? <span className="badge-active">In your routes</span>
                  : it.mine ? <span className="badge-inactive">Yours</span>
                    : (
                      <button type="button" className="btn-primary btn-sm" onClick={() => setOpenId(it.id)}>
                        <IconPlus className="w-3.5 h-3.5" /> Add
                      </button>
                    )}
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="flex flex-col items-center gap-3">
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} total={data.total} label="routes" onPage={setPage} />
        )}
        <span className="text-[13px] text-gray-500">Something wrong with a route? Open it and use “Report”.</span>
      </div>
    </div>
  );
}

/**
 * A kártya tárgy-jelölője (a sablon `.item-pill`-je): egy tárgynál az angol neve és a
 * darabszám, többnél „N items”; elöl az ikonjaik, ha a backend adott ikont.
 */
function ItemPill({ items, names }) {
  const first = items[0];
  const n = first?.tpl ? names?.items?.[first.tpl] : null;
  const text = items.length > 1
    ? itemShort(items)
    : `${n?.name || ITEM_KIND_LABEL[first.kind] || 'Item'}${first.count > 1 ? ` ×${first.count}` : ''}`;
  const icons = items.map(x => names?.items?.[x.tpl]).filter(x => x?.icon).slice(0, 3);
  const title = items.map(x => {
    const nm = x.tpl ? names?.items?.[x.tpl]?.name : null;
    return `${x.count > 1 ? `${x.count} × ` : ''}${nm || ITEM_KIND_LABEL[x.kind] || x.kind}`;
  }).join(', ');
  return (
    <span title={title}
          className="min-w-0 inline-flex items-center gap-1.5 h-[26px] pl-1 pr-[9px] rounded-lg bg-warn/[0.07] border border-warn/25 text-xs text-[#f0cf8a]">
      {icons.length > 0
        ? icons.map(x => <img key={x.icon} src={x.icon} alt="" loading="lazy" className="w-[18px] h-[18px] flex-none" />)
        : <IconPackage className="w-3.5 h-3.5 ml-1 text-warn flex-none" />}
      <span className="truncate">{text}</span>
    </span>
  );
}

// ── EGY TÉTEL ─────────────────────────────────────────────────────────────────
function LibraryItem({ id, onBack }) {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/library/${id}`);
      setD(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this route.');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  // A tétel a lista HELYÉRE nyílik: a lista aljáról nyitva különben középen állnánk.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [id]);

  const add = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/library/${id}/add`);
      toastRef.current.success(res.data.already ? 'It is already in your routes.' : `Added as ${res.data.key}. Your PCs get it on their next sync.`);
      setConfirm(false);
      await load();
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not add this route.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-6">
      <button type="button" className="self-start flex items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-50 transition-colors" onClick={onBack}>
        <IconChevron open={false} className="w-3.5 h-3.5 rotate-180" /> Route library
      </button>

      {error ? (
        <div className="card p-6 text-sm text-[#ff8a80]">{error}</div>
      ) : !d ? (
        <div className="card py-12 flex justify-center"><LoadingSpinner label="Loading…" /></div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <h2 className="text-[clamp(26px,3vw,32px)] font-semibold tracking-[-0.03em] text-gray-50 break-words">{d.title}</h2>
            <p className="text-sm text-gray-500">
              by <span className="text-gray-100">{d.author}</span>{d.mine ? ' (you)' : ''} · published {utcDate(d.publishedAt)}
              {d.updatedAt && d.updatedAt !== d.publishedAt ? ` · updated ${utcDate(d.updatedAt)}` : ''} · added {d.adds} {d.adds === 1 ? 'time' : 'times'}
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
            <div className="flex flex-col gap-5">
              {d.changes && (
                <PanelCard title={`What changed in v${d.version}`}
                           actions={<span className="font-mono text-xs text-gray-600">{utcDate(d.updatedAt)}</span>}>
                  <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">{d.changes}</p>
                </PanelCard>
              )}
              {d.description && (
                <PanelCard title="About">
                  <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">{d.description}</p>
                </PanelCard>
              )}
              <PanelCard title="Steps" bodyClassName="flex flex-col gap-3">
                <StepsList maps={d.summary.maps} entry={d.summary.entry} names={d.summary.names} />
                <KillTargets kills={d.summary.kills} names={d.summary.names} />
              </PanelCard>
              <PanelCard title="Uses items">
                <ItemsBox items={d.summary.items} title={null} names={d.summary.names} />
              </PanelCard>
            </div>

            <aside className="card p-5 flex flex-col gap-4 lg:sticky lg:top-[84px]" aria-label="Add this route">
              {d.mine ? (
                <span className="text-sm text-gray-400">This is your route. Manage it on your Routes page.</span>
              ) : d.inMine ? (
                <>
                  <span className="badge-active self-start">In your routes</span>
                  <span className="font-mono text-[13px] text-gray-100 break-all">{d.inMine}.route</span>
                </>
              ) : (
                <>
                  <button type="button" className="btn-primary w-full" onClick={() => setConfirm(true)}><IconPlus className="w-4 h-4" /> Add to my routes</button>
                  <span className="text-[12.5px] text-gray-500">
                    You get your own copy in the <span className="font-mono text-gray-100">Community</span> folder.
                    The author's later edits never change it.
                  </span>
                </>
              )}
              {!d.mine && (
                d.reported ? (
                  <span className="text-[12.5px] text-gray-500">You reported this route. Staff will look at it.</span>
                ) : (
                  <button type="button" className="btn-ghost btn-sm px-0 self-start text-gray-400 hover:text-gray-50 hover:bg-transparent" onClick={() => setReporting(true)}>
                    <IconFlag className="w-4 h-4" /> Report this route
                  </button>
                )
              )}
            </aside>
          </div>
        </>
      )}

      {confirm && d && (
        <Modal
          title="Add this route?"
          subtitle={`${d.title} · by ${d.author}`}
          icon={d.summary.items.length ? <IconAlert className="w-5 h-5" /> : <IconBook className="w-5 h-5" />}
          tone={d.summary.items.length ? 'warn' : 'brand'}
          size="lg"
          onClose={() => setConfirm(false)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setConfirm(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={busy} onClick={add}>Add to my routes</button>
            </div>
          }
        >
          <div className="flex flex-col gap-4">
            {d.summary.items.length > 0 ? (
              <ItemsBox items={d.summary.items} names={d.summary.names}
                        title="When it runs, this route uses items from your bag:" />
            ) : (
              <ItemsBox items={[]} />
            )}
            <div className="flex flex-col gap-1">
              <span className="label mb-0">Saved as</span>
              <span className="font-mono text-[13px] text-gray-50 break-all">{d.savedAs}.route</span>
            </div>
            {BOT_ASKS_BEFORE_FIRST_RUN && (
              <div className="flex gap-2.5 items-start text-[12.5px] text-gray-500">
                <IconShield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                The bot asks you once more before this route runs for the first time.
              </div>
            )}
          </div>
        </Modal>
      )}

      {reporting && d && (
        <ReportModal id={id} title={d.title} onClose={() => setReporting(false)} onDone={() => { setReporting(false); load(); }} />
      )}
    </div>
  );
}

function ReportModal({ id, title, onClose, onDone }) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/library/${id}/report`, { reason });
      toast.success('Thanks — staff will look at it.');
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the report.');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      title="Report this route"
      subtitle={title}
      icon={<IconFlag className="w-5 h-5" />}
      tone="warn"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || reason.trim().length < 3} onClick={send}>Send report</button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {REPORT_REASONS.map(r => (
            <button key={r} type="button" onClick={() => setReason(r)}
                    aria-pressed={reason === r}
                    className={`h-7 px-2.5 rounded-lg border text-[12.5px] transition-colors ${reason === r ? 'bg-gray-800 border-line-strong text-gray-50' : 'border-line text-gray-400 hover:text-gray-50'}`}>
              {r}
            </button>
          ))}
        </div>
        <label htmlFor="rp-reason" className="label mb-0">What is wrong?</label>
        <input id="rp-reason" className="input" maxLength={255} value={reason} onChange={e => setReason(e.target.value)} />
        {error && <p className="text-sm text-[#ff8a80]">{error}</p>}
      </div>
    </Modal>
  );
}
